import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useWorkspaceStore, type Workspace } from '@/stores/workspace';

export function OnboardingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setCurrent = useWorkspaceStore((s) => s.setCurrent);
  const [tab, setTab] = useState<'create' | 'join'>('create');
  const [familyName, setFamilyName] = useState('我家');
  const [currency, setCurrency] = useState('AUD');
  const [code, setCode] = useState('');

  // Pre-fill invite code if user came from a /join?code=... link
  useEffect(() => {
    const pending = sessionStorage.getItem('pending-invite');
    if (pending) {
      setTab('join');
      setCode(pending);
      sessionStorage.removeItem('pending-invite');
    }
  }, []);

  const createMutation = useMutation({
    mutationFn: (input: { name: string; default_currency: string }) =>
      api.post<Workspace>('/workspaces', input),
    onSuccess: (ws) => {
      setCurrent(ws);
      navigate('/home', { replace: true });
    },
  });

  const joinMutation = useMutation({
    mutationFn: (input: { code: string }) =>
      api.post<Workspace>('/workspaces/join', input),
    onSuccess: (ws) => {
      setCurrent(ws);
      navigate('/home', { replace: true });
    },
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-md rounded-2xl border bg-card p-6 shadow-sm">
        <h1 className="mb-1 text-2xl font-semibold">{t('auth.onboardingWelcome')}</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          {t('auth.onboardingSubtitleAlt')}
        </p>

        <div className="mb-4 flex gap-1 rounded-md bg-muted p-1">
          <button
            onClick={() => setTab('create')}
            className={`flex-1 rounded-sm py-1.5 text-sm transition-colors ${
              tab === 'create' ? 'bg-background shadow-sm' : 'text-muted-foreground'
            }`}
          >
            {t('auth.onboardingTabCreate')}
          </button>
          <button
            onClick={() => setTab('join')}
            className={`flex-1 rounded-sm py-1.5 text-sm transition-colors ${
              tab === 'join' ? 'bg-background shadow-sm' : 'text-muted-foreground'
            }`}
          >
            {t('auth.onboardingTabJoin')}
          </button>
        </div>

        {tab === 'create' ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              createMutation.mutate({ name: familyName, default_currency: currency });
            }}
            className="space-y-3"
          >
            <input
              type="text"
              placeholder={t('auth.onboardingCreateNamePlaceholder')}
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              required
              className="w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="AUD">AUD - Australian Dollar</option>
              <option value="USD">USD - US Dollar</option>
              <option value="CNY">CNY - Chinese Yuan</option>
              <option value="EUR">EUR - Euro</option>
              <option value="GBP">GBP - British Pound</option>
              <option value="JPY">JPY - Japanese Yen</option>
            </select>
            {createMutation.isError && (
              <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">
                {(createMutation.error as Error).message}
              </div>
            )}
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="w-full rounded-md bg-primary py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {createMutation.isPending ? t('auth.submitting') : t('auth.onboardingCreateSubmitAlt')}
            </button>
          </form>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              joinMutation.mutate({ code: code.trim().toUpperCase() });
            }}
            className="space-y-3"
          >
            <input
              type="text"
              placeholder={t('auth.onboardingJoinCodePlaceholder')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              className="w-full rounded-md border bg-background px-3 py-2 text-sm uppercase outline-none focus:ring-2 focus:ring-ring"
            />
            {joinMutation.isError && (
              <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">
                {(joinMutation.error as Error).message}
              </div>
            )}
            <button
              type="submit"
              disabled={joinMutation.isPending}
              className="w-full rounded-md bg-primary py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {joinMutation.isPending ? t('auth.submitting') : t('auth.onboardingJoinSubmitAlt')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
